<template>
  <div>
    <page-header header="User Settings" subheader="Set up your user ID, contact and office details" />

    <section class="flex flex-wrap mb-8 -mx-4">
      <div class="w-full xl:w-2/3 px-4 mb-6 xl:mb-0">
        <panel with-header>
          <header slot="header" class="flex items-center border-b pb-4">
            <!-- <icon class="icon-sm bg-tertiary text-white mr-6" name="Rebranding"></icon> -->
            <div class="w-12 h-12 flex-no-shrink bg-tertiary rounded-lg mr-6 text-white">
              <!-- <i class="fas fa-users fa-2x"></i> -->
              <icon name="staff" class="p-1" />
            </div>
            <h1 class="text-black text-2xl font-semibold">Account Settings</h1>
          </header>
          <div class="flex flex-wrap -mx-4">
            <div class="w-full md:w-1/2 px-4 mb-6 md:mb-0">
              <div class="mb-4">
                <p class="mb-4">
                  <strong>User ID:</strong> {{ profile.id }}
                </p>
                <label class="block mb-1 text-grey-darkest text-sm font-normal">First Name</label>
                <input v-model="profile.first_name" type="text" placeholder="First Name" class="input w-full mb-3">

                <label class="block mb-1 text-grey-darkest text-sm font-normal">Surname</label>
                <input v-model="profile.surname" type="text" placeholder="Surname" class="input w-full mb-3">
              </div>
              <div class="mb-4">
                <label class="block mb-1 text-grey-darkest text-sm font-normal">Position</label>
                <input v-model="profile.position" type="text" class="input w-full mb-3">
              </div>
              <div class="mb-6">
                <label class="block mb-1 text-grey-darkest text-sm font-normal">Email Address</label>
                <input v-model="profile.email" type="email" placeholder="Email Address" class="input w-full mb-3"
                  :class="{ 'border-red-dark': this.errorBag.get('email') }">
                <small class="text-xs text-red-dark text-medium mb-3" v-text="this.errorBag.get('email')"></small>
              </div>
              <div class="mb-6">
                <label class="block mb-1 text-grey-darkest text-sm font-normal">Contact Number</label>
                <input v-model="profile.phone" type="text" placeholder="Contact Number" class="input w-full mb-3">
              </div>
              <div class="mb-6">
                <label class="block mb-1 text-grey-darkest text-sm font-normal">Mobile Number</label>
                <input v-model="profile.mobile" type="text" placeholder="Mobile Number" class="input w-full mb-3">
              </div>

              <div class="mb-6 flex flex-col items-start">

                <p class="italic text-sm mb-3">Leave the password fields blank if you don't want to change your password.</p>

                <label class="block mb-1 text-grey-darkest text-sm font-normal">Password</label>
                <input v-model="profile.password" type="password" placeholder="Password" class="input w-full mb-3"
                  :class="{ 'border-red-dark': this.errorBag.get('password') }">
                <label class="block mb-1 text-grey-darkest text-sm font-normal">Confirm Password</label>
                <input v-model="profile.password_confirmation" type="password" placeholder="Confirm Password" class="input w-full mb-3"
                  :class="{ 'border-red-dark': this.errorBag.get('password') }">
                <small class="block text-xs text-red-dark text-medium mb-3" v-text="this.errorBag.get('password')"></small>
                <button class="bg-tertiary hover:border-tertiary text-white py-2 px-10 rounded text-xl flex items-center justify-center" :disabled="savingProfile" @click="saveProfile">
                  <loader v-if="savingProfile"/>
                  <template v-else>Save</template>
                </button>
              </div>
            </div>
            <div class="w-full md:w-1/2 px-6">
              <div class="mb-6">
                <label class="block mb-2 text-grey-darkest text-lg font-normal">Profile Picture:</label>
                <div class="relative flex items-end justify-center w-48 h-48 bg-grey-lighter border">
                  <img :src="profile.photo" alt="profile pic" class="absolute pin-t pin-l">
                  <span class="relative mb-4 w-3/5 text-center text-sm text-white font-bold">
                    <i class="fas fa-camera"></i>
                    <br>
                    Drop an image to change
                  </span>
                  <input type="file" name="profile_pic" id="profile_pic" class="absolute w-full h-full opacity-0" @change="photoChanged">
                </div>
              </div>
              <button class="bg-red hover:border-tertiary text-white py-2 px-5 rounded text-sm flex items-center justify-center" :disabled="deletePhotoLoader" @click="deletePhoto">
                <loader v-if="deletePhotoLoader"/>
                <template v-else>Delete Photo</template>
              </button>
            </div>
          </div>
        </panel>

        <user-admin v-if="profile.type <= 4" class="mt-8 mb-8" />

        <social-accounts />
      </div>

      <div class="w-full xl:w-1/3 px-4"> <!-- Associated Office -->
        <panel with-header>
          <header slot="header" class="flex items-center pb-4 mb-6 border-b">
            <!-- <icon class="icon-sm bg-tertiary text-white mr-6" name="Rebranding"></icon> -->
            <div class="w-12 h-12 flex-no-shrink bg-tertiary rounded-lg mr-6 text-white">
              <!-- <i class="far fa-building fa-2x"></i> -->
              <icon name="associated_office" class="p-1 -ml-1" />
            </div>
            <h1 class="text-black text-2xl font-semibold">Associated Office</h1>
          </header>
          <offices v-if="offices" :offices="offices" />
          <div v-else class="flex flex-col justify-center items-center">
            <h3 class="mt-4 mb-8 text-lg font-semibold text-center">You aren't assigned to an office</h3>
            <!-- TODO: add click event, we need to know how this is going to notify admin -->
            <button @click="notifyAdmin" class="bg-grey-lighter hover:bg-grey-light border hover:border-grey px-4 py-2 text-xs uppercase tracking-wide">
              <i class="fas fa-envelope mr-2"></i>
              Notify Admin
            </button>
          </div>
        </panel>
      </div> <!-- End Associated Office -->
    </section>

    <!-- <section class="flex mb-8 -mx-4">
      <div class="w-1/3 px-4">
        <panel class="bg-teal">
          <header class="flex items-center">
            <icon class="icon-xs text-teal-darker mr-2" name="KnowledgeHub"></icon>
            <h3 class="text-teal-darker text-lg font-medium">Associate Scheme Training</h3>
          </header>
        </panel>
      </div>
      <div class="w-1/3 px-4">
        <panel class="bg-teal">
          <header class="flex items-center">
            <icon class="icon-xs text-teal-darker mr-2" name="KnowledgeHub"></icon>
            <h3 class="text-teal-darker text-lg font-medium">Associate Scheme Training</h3>
          </header>
        </panel>
      </div>
      <div class="w-1/3 px-4">
        <panel class="bg-teal">
          <header class="flex items-center">
            <icon class="icon-xs text-teal-darker mr-2" name="KnowledgeHub"></icon>
            <h3 class="text-teal-darker text-lg font-medium">Associate Scheme Training</h3>
          </header>
        </panel>
      </div>
    </section> -->
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import Offices from '@/components/Offices'
import UserAdmin from '@/components/UserAdmin/UserAdmin'
import SocialAccounts from '@/components/SocialAccounts'
import ErrorBag from '@/ErrorBag'
import Loader from '@/components/Loader'

export default {
  components: {
    Offices,
    UserAdmin,
    SocialAccounts,
    Loader
  },

  data () {
    return {
      errorBag: new ErrorBag(),
      savingProfile: false,
      deletePhotoLoader: false
    }
  },

  computed: {
    ...mapState([
      'profile', 'userOfficeList', 'selectedOffice'
    ]),
    offices () {
      // return this.userOfficeList
      return [this.selectedOffice]
    },
    password () {
      return this.profile.password
    }
  },

  watch: {
    password (newPassword) {
      if (!newPassword) {
        delete this.profile.password
        delete this.profile.password_confirmation
        this.errorBag.delete('password')
      }
    }
  },

  created () {
    this.fetchProfile()
  },

  methods: {
    ...mapActions([
      'fetchProfile',
      'updateProfile',
      'updateProfilePic',
      'notifyAdmin',
      'displayToastNotification',
      'deleteProfilePic'
    ]),
    saveProfile () {
      this.savingProfile = true
      this.updateProfile(this.profile)
        .then(response => {
          this.errorBag.clear()
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check',
            title: 'Profile Updated',
            info: 'Your profile has been updated successfully.'
          })
        })
        .catch(error => this.errorBag.store(error.response.data))
        .finally(() => { this.savingProfile = false })
    },
    photoChanged (e) {
      let files = e.target.files

      let data = new FormData()
      data.append('_method', 'PATCH')
      data.append('new_photo', files[0])

      this.updateProfilePic(data)
    },

    deletePhoto () {
      this.deletePhotoLoader = true
      this.deleteProfilePic(this.profile)
        .then(response => {
          this.errorBag.clear()
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check',
            title: 'Profile Updated',
            info: 'Your profile photo has been deleted successfully.'
          })
        })
        .catch(error => this.errorBag.store(error.response.data))
        .finally(() => { this.deletePhotoLoader = false })
    }
  }
}
</script>
